import { addDays, eachDay, weekStart, type ISODate } from './dates';
import type { DayCapacity } from './capacity';
import type { BillingType, EntryStatus } from './types';

/**
 * Key figures. Pure functions: the server collects the data, these functions compute.
 * Definitions are documented in docs/KPI.md and must stay stable, because
 * Power BI reports and exported Excel files use the same meaning.
 */

export interface KpiEntry {
  personId: string;
  projectId: string;
  workDate: ISODate;
  minutes: number;
  billingType: BillingType;
  status: EntryStatus;
  submittedAt?: string | null;
  approvedAt?: string | null;
  rejectCount?: number;
}

export interface MinutesByType {
  total: number;
  billable: number;
  included: number;
  nonBillable: number;
}

export function sumByType(entries: Pick<KpiEntry, 'minutes' | 'billingType'>[]): MinutesByType {
  const r: MinutesByType = { total: 0, billable: 0, included: 0, nonBillable: 0 };
  for (const e of entries) {
    r.total += e.minutes;
    if (e.billingType === 'billable') r.billable += e.minutes;
    else if (e.billingType === 'included') r.included += e.minutes;
    else r.nonBillable += e.minutes;
  }
  return r;
}

/**
 * Hour types of booked time (request 19 of round 4), each booked minute in exactly one of them:
 * - billable: on the customer document of an hourly project,
 * - included: covered by a flat fee (on the document of a flat-fee project, not charged separately),
 * - nonBillableProject: work in a customer project that is not charged (e.g. a check), never on a customer document,
 * - internal: the internal project (R&D, training, office): not project work.
 * Billed hours (abgerechnet) are a different figure: what the office put on the document, they may differ from the
 * booked hours (e.g. 5 h booked, 8 h billed) and stay in the project and billing reports, not in personal figures.
 */
export interface HourTypes {
  total: number;
  billable: number;
  included: number;
  nonBillableProject: number;
  internal: number;
}

export function hourTypes(entries: { minutes: number; billingType: BillingType; internal: boolean }[]): HourTypes {
  const r: HourTypes = { total: 0, billable: 0, included: 0, nonBillableProject: 0, internal: 0 };
  for (const e of entries) {
    r.total += e.minutes;
    if (e.internal) r.internal += e.minutes;
    else if (e.billingType === 'billable') r.billable += e.minutes;
    else if (e.billingType === 'included') r.included += e.minutes;
    else r.nonBillableProject += e.minutes;
  }
  return r;
}

/** Below this share of evaluated hours the plan/actual ratio of a person is shown with a note (few tasks had a plan). */
export const PLAN_COVERAGE_LOW = 0.5;

export function ratio(part: number, whole: number): number {
  return whole > 0 ? part / whole : Number.NaN;
}

/** Load level used for colors in the workload views. */
export type LoadLevel = 'none' | 'low' | 'ok' | 'high' | 'over';

export function loadLevel(plannedOrBooked: number, capacity: number): LoadLevel {
  if (capacity <= 0) return plannedOrBooked > 0 ? 'over' : 'none';
  const r = plannedOrBooked / capacity;
  if (r < 0.7) return 'low';
  if (r <= 1.0) return 'ok';
  if (r <= 1.1) return 'high';
  return 'over';
}

export interface PersonKpi {
  personId: string;
  /** Soll (target hours) of the period: time off in lieu counts as target, see capacity.ts */
  capacityMinutes: number;
  /** time off in lieu (Freizeitausgleich) taken in the period, part of capacityMinutes */
  compensationMinutes: number;
  bookedMinutes: number;
  billableMinutes: number;
  includedMinutes: number;
  nonBillableMinutes: number;
  /** booked / target */
  utilization: number;
  /** billable / booked */
  billableShare: number;
  /** (billable + included) / booked: time spent on customer projects */
  projectShare: number;
  /** booked - target (Soll/Ist): extra hours worked minus time off in lieu taken */
  balanceMinutes: number;
  draftMinutes: number;
  submittedMinutes: number;
  approvedMinutes: number;
  weeksDue: number;
  weeksOnTime: number;
  /** weeksOnTime / weeksDue */
  punctuality: number;
  entriesRejected: number;
  rejectionRate: number;
  avgApprovalDays: number;
}

export interface PersonKpiInput {
  personId: string;
  from: ISODate;
  to: ISODate;
  entries: KpiEntry[];
  capacity: DayCapacity[];
  /** Current time, ISO timestamp. */
  now: string;
  submitDeadlineDays: number;
}

export function personKpi(input: PersonKpiInput): PersonKpi {
  const entries = input.entries.filter(
    (e) => e.personId === input.personId && e.workDate >= input.from && e.workDate <= input.to,
  );
  const cap = input.capacity.filter((d) => d.date >= input.from && d.date <= input.to);
  const capacityMinutes = cap.reduce((s, d) => s + (d.targetMinutes ?? d.capacityMinutes), 0);
  const compensationMinutes = cap.reduce((s, d) => s + (d.compensationMinutes ?? 0), 0);
  const t = sumByType(entries);
  let draft = 0;
  let submitted = 0;
  let approved = 0;
  for (const e of entries) {
    if (e.status === 'draft' || e.status === 'rejected') draft += e.minutes;
    else if (e.status === 'submitted' || e.status === 'approved_l1') submitted += e.minutes;
    else approved += e.minutes;
  }

  // Punctuality per ISO week: a week is due when it had capacity or entries and its deadline has passed.
  const nowDate = input.now.slice(0, 10);
  const weeks = new Set<ISODate>();
  for (const d of eachDay(input.from, input.to)) weeks.add(weekStart(d));
  let weeksDue = 0;
  let weeksOnTime = 0;
  for (const ws of weeks) {
    const we = addDays(ws, 6);
    const deadline = addDays(we, input.submitDeadlineDays);
    if (deadline >= nowDate) continue;
    const weekCap = cap.filter((d) => d.date >= ws && d.date <= we).reduce((s, d) => s + (d.targetMinutes ?? d.capacityMinutes), 0);
    const weekEntries = entries.filter((e) => e.workDate >= ws && e.workDate <= we);
    if (weekCap <= 0 && weekEntries.length === 0) continue;
    weeksDue++;
    if (weekEntries.length === 0) continue;
    const onTime = weekEntries.every(
      (e) => e.submittedAt && e.submittedAt.slice(0, 10) <= deadline && e.status !== 'draft',
    );
    if (onTime) weeksOnTime++;
  }

  const rejected = entries.filter((e) => (e.rejectCount ?? 0) > 0).length;
  const lags: number[] = [];
  for (const e of entries) {
    if (e.submittedAt && e.approvedAt) {
      lags.push((Date.parse(e.approvedAt) - Date.parse(e.submittedAt)) / 86_400_000);
    }
  }

  return {
    personId: input.personId,
    capacityMinutes,
    compensationMinutes,
    bookedMinutes: t.total,
    billableMinutes: t.billable,
    includedMinutes: t.included,
    nonBillableMinutes: t.nonBillable,
    utilization: ratio(t.total, capacityMinutes),
    billableShare: ratio(t.billable, t.total),
    projectShare: ratio(t.billable + t.included, t.total),
    balanceMinutes: t.total - capacityMinutes,
    draftMinutes: draft,
    submittedMinutes: submitted,
    approvedMinutes: approved,
    weeksDue,
    weeksOnTime,
    punctuality: ratio(weeksOnTime, weeksDue),
    entriesRejected: rejected,
    rejectionRate: ratio(rejected, entries.length),
    avgApprovalDays: lags.length ? lags.reduce((s, x) => s + x, 0) / lags.length : Number.NaN,
  };
}

export interface ProjectKpiEntry extends KpiEntry {
  billedMinutes?: number | null;
  salesRateCents?: number | null;
  costRateCents?: number | null;
}

export interface ProjectKpi {
  totalMinutes: number;
  billableMinutes: number;
  includedMinutes: number;
  nonBillableMinutes: number;
  approvedMinutes: number;
  billedMinutes: number;
  unbilledApprovedBillableMinutes: number;
  budgetMinutes: number | null;
  budgetUsed: number;
  salesValueCents: number;
  unbilledValueCents: number;
  costCents: number | null;
  /** Minutes without a cost rate (cost is understated by these). */
  costMissingMinutes: number;
  byPerson: { personId: string; minutes: number; billable: number }[];
  byMonth: { month: string; minutes: number; billable: number }[];
}

export function projectKpi(
  entries: ProjectKpiEntry[],
  budgetMinutes: number | null,
  includeCost: boolean,
): ProjectKpi {
  const t = sumByType(entries);
  let approved = 0;
  let billed = 0;
  let unbilled = 0;
  let sales = 0;
  let unbilledValue = 0;
  let cost = 0;
  let costMissingMinutes = 0;
  const byPerson = new Map<string, { personId: string; minutes: number; billable: number }>();
  const byMonth = new Map<string, { month: string; minutes: number; billable: number }>();
  for (const e of entries) {
    const billableMin = e.billingType === 'billable' ? (e.billedMinutes ?? e.minutes) : 0;
    if (e.status === 'approved' || e.status === 'billed') approved += e.minutes;
    if (e.status === 'billed') billed += e.billedMinutes ?? e.minutes;
    if (e.status === 'approved' && e.billingType === 'billable') {
      unbilled += billableMin;
      unbilledValue += Math.round((billableMin / 60) * (e.salesRateCents ?? 0));
    }
    sales += Math.round((billableMin / 60) * (e.salesRateCents ?? 0));
    if (includeCost) {
      if (e.costRateCents == null) costMissingMinutes += e.minutes;
      cost += Math.round((e.minutes / 60) * (e.costRateCents ?? 0));
    }
    // hours by person and month are working time: recorded minutes, not the hours invoiced
    const recordedBillable = e.billingType === 'billable' ? e.minutes : 0;
    const p = byPerson.get(e.personId) ?? { personId: e.personId, minutes: 0, billable: 0 };
    p.minutes += e.minutes;
    p.billable += recordedBillable;
    byPerson.set(e.personId, p);
    const mk = e.workDate.slice(0, 7);
    const m = byMonth.get(mk) ?? { month: mk, minutes: 0, billable: 0 };
    m.minutes += e.minutes;
    m.billable += recordedBillable;
    byMonth.set(mk, m);
  }
  return {
    totalMinutes: t.total,
    billableMinutes: t.billable,
    includedMinutes: t.included,
    nonBillableMinutes: t.nonBillable,
    approvedMinutes: approved,
    billedMinutes: billed,
    unbilledApprovedBillableMinutes: unbilled,
    budgetMinutes,
    budgetUsed: budgetMinutes ? t.total / budgetMinutes : Number.NaN,
    salesValueCents: sales,
    unbilledValueCents: unbilledValue,
    costCents: includeCost ? cost : null,
    costMissingMinutes: includeCost ? costMissingMinutes : 0,
    byPerson: [...byPerson.values()].sort((a, b) => b.minutes - a.minutes),
    byMonth: [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month)),
  };
}

/* ------------------------------------------------------------------------------------------------
 * Plan / Ist per task and the plan/actual ratio (Plan-Ist-Quote)
 *
 * Plan of a task (Vorgabe) = hours entered in Projektzeit by the project lead or planners (Planung). Plan 0 means
 * "not evaluated" (e.g. cancelled task).
 * A finished task (Forma: completed/closed, platform: closed) counts in the period in which it was finished.
 * Earned hours of a person = plan x the person's share of all hours booked on the task.
 * Ratio of a person = earned hours / the person's booked hours. For one task this equals plan / all hours
 * booked on the task, the same value for everybody who worked on it: the figure describes how the tasks a
 * person worked on went against their plan, not how fast the person works. Over several tasks it is the
 * hour-weighted mean of these task ratios. Tasks worked on mostly alone (>= 80 % of the hours) are counted
 * separately (soloTasks, soloRatio); only there the value says something about the person's own work.
 * 200 % = the task took half of the planned time.
 * All figures use recorded hours; hours invoiced to the client (billed hours) are not used here.
 * ---------------------------------------------------------------------------------------------- */

export interface PlannedTask {
  taskId: string;
  plannedMinutes: number | null;
  /** Date the task was finished (YYYY-MM-DD), null while active. */
  doneOn: ISODate | null;
}

export interface TaskPersonMinutes {
  taskId: string;
  personId: string;
  minutes: number;
}

export interface EfficiencyItem {
  taskId: string;
  doneOn: ISODate;
  plannedMinutes: number;
  /** All persons, whole lifetime of the task. */
  taskMinutes: number;
  personMinutes: number;
  /** Plan x share of the person. */
  earnedMinutes: number;
  /** earned / personMinutes (= plan / taskMinutes) */
  ratio: number;
}

export interface EfficiencySummary {
  tasks: number;
  earnedMinutes: number;
  actualMinutes: number;
  /** Plan/actual ratio of the tasks worked on; NaN without evaluated tasks. */
  ratio: number;
  /** earned - actual: positive = time saved against plan */
  savedMinutes: number;
  /** Tasks with at least SOLO_SHARE of their hours booked by the person, and their ratio. */
  soloTasks: number;
  soloRatio: number;
}

/** Share of a task's hours from which the task counts as worked on mostly alone. */
export const SOLO_SHARE = 0.8;

/**
 * Bands of the plan/actual ratio. Every screen that colors the ratio uses these, so a person and the management see
 * the same color for the same value: below EFFICIENCY_LOW a hint (the task took clearly longer than planned: plan too
 * small, or obstacles), from EFFICIENCY_GOOD green (plan met or better), from EFFICIENCY_FAST a finished task counts as
 * "clearly under plan" (check the plan, or share the solution). The bands only color and count; nothing is stored.
 */
export const EFFICIENCY_LOW = 0.8;
export const EFFICIENCY_GOOD = 1;
export const EFFICIENCY_FAST = 1.5;

/** Color of a plan/actual ratio ('warn' = hint, 'good' = plan met or better, undefined = in between or no value). */
export function efficiencyTone(ratio: number | null | undefined): 'good' | 'warn' | undefined {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return undefined;
  return ratio >= EFFICIENCY_GOOD ? 'good' : ratio < EFFICIENCY_LOW ? 'warn' : undefined;
}


export function personEfficiency(
  personId: string,
  tasks: PlannedTask[],
  minutes: TaskPersonMinutes[],
  from: ISODate,
  to: ISODate,
): { summary: EfficiencySummary; items: EfficiencyItem[] } {
  const totals = new Map<string, number>();
  const mine = new Map<string, number>();
  for (const m of minutes) {
    totals.set(m.taskId, (totals.get(m.taskId) ?? 0) + m.minutes);
    if (m.personId === personId) mine.set(m.taskId, (mine.get(m.taskId) ?? 0) + m.minutes);
  }
  const items: EfficiencyItem[] = [];
  for (const t of tasks) {
    if (!t.plannedMinutes || t.plannedMinutes <= 0 || !t.doneOn) continue;
    if (t.doneOn < from || t.doneOn > to) continue;
    const taskMinutes = totals.get(t.taskId) ?? 0;
    const personMinutes = mine.get(t.taskId) ?? 0;
    if (taskMinutes <= 0 || personMinutes <= 0) continue;
    const earned = Math.round((t.plannedMinutes * personMinutes) / taskMinutes);
    items.push({
      taskId: t.taskId,
      doneOn: t.doneOn,
      plannedMinutes: t.plannedMinutes,
      taskMinutes,
      personMinutes,
      earnedMinutes: earned,
      ratio: t.plannedMinutes / taskMinutes,
    });
  }
  items.sort((a, b) => b.ratio - a.ratio);
  const earnedMinutes = items.reduce((s, i) => s + i.earnedMinutes, 0);
  const actualMinutes = items.reduce((s, i) => s + i.personMinutes, 0);
  const solo = items.filter((i) => i.personMinutes >= SOLO_SHARE * i.taskMinutes);
  return {
    summary: {
      tasks: items.length,
      earnedMinutes,
      actualMinutes,
      ratio: ratio(earnedMinutes, actualMinutes),
      savedMinutes: earnedMinutes - actualMinutes,
      soloTasks: solo.length,
      soloRatio: ratio(
        solo.reduce((s, i) => s + i.earnedMinutes, 0),
        solo.reduce((s, i) => s + i.personMinutes, 0),
      ),
    },
    items,
  };
}

/** Plan use of a task: booked / plan (1 = plan used up, > 1 = over plan). */
export function planUse(bookedMinutes: number, plannedMinutes: number | null): number {
  return plannedMinutes && plannedMinutes > 0 ? bookedMinutes / plannedMinutes : Number.NaN;
}

/* ------------------------------------------------------------------------------------------------
 * Booking state of a finished week: under-booked (hours missing) or over capacity (overtime).
 * ---------------------------------------------------------------------------------------------- */

export type WeekBookingState = 'under' | 'over' | 'ok';

/**
 * `targetMinutes` is the Soll of the week, `compensationMinutes` the time off in lieu taken in it. Missing
 * hours are measured against the target minus time off in lieu (nobody books on such a day), extra work
 * against the full target (time off in lieu in the same week offsets the extra hours).
 */
export function weekBookingState(bookedMinutes: number, targetMinutes: number, toleranceMinutes: number, compensationMinutes = 0): WeekBookingState | null {
  if (targetMinutes <= 0 && bookedMinutes <= 0) return null;
  if (bookedMinutes < targetMinutes - compensationMinutes - toleranceMinutes) return 'under';
  if (bookedMinutes > targetMinutes + toleranceMinutes) return 'over';
  return 'ok';
}
